import { acceptHMRUpdate, defineStore } from 'pinia'
import { apiSupport } from '@/helpers/api.js'
import { handleApiError } from '@/services/errorHandler'
import { useStrapiStore } from '@/stores/StrapiStore'
import { useUserStore } from '@/stores/UserStore'
import { attachmentContentType, attachmentRejection, isOpenConversation, isWhatsNewAsk, releaseNotesFromChangelog, supportSubject } from '@/helpers/support'
import {
  closeSupportSocket,
  initSupportSocket,
  onSupportMessage,
  onSupportProgress,
  onSupportStatus,
  subscribeToConversation,
  unsubscribeFromConversation,
} from '@/helpers/supportSocket'
import { SUPPORT_ATTACHMENT_MAX_FILES, SUPPORT_RESOLUTION, SUPPORT_SENDER, SUPPORT_SESSION_KEY } from '@/constants/support'

/**
 * The Help Hub's Chat tab, talking to the CDP Support service's widget API directly.
 *
 * That API is the support product's own, undocumented and unversioned — it exists to serve
 * their React bubble, not us. So every read is defensive about shape, and a support outage
 * is contained here: it sets `failed` and the tab shows a retry, it never propagates into
 * the app (which is also why `apiSupport` has no interceptor — see helpers/api.js).
 */
export const useSupportStore = defineStore('Support', {
  state: () => ({
    // The support session JWT, kept per browser so a reload rejoins the same conversations.
    sessionToken: localStorage.getItem(SUPPORT_SESSION_KEY) || null,
    // The email the session was tied to, so switching CDP accounts re-identifies instead of
    // writing one dealer's questions into another's history.
    identifiedAs: null,

    conversations: [],
    conversationsLoading: false,
    conversationsFailed: false,
    // The list has come back at least once: only then can a ticket the URL names be called missing.
    conversationsLoaded: false,
    // `loadUnread` runs once per app load; the Chat tab's own `load()` is separate.
    unreadRequested: false,

    questions: [],

    // The open thread. `activeId` is null for a thread that has not reached support yet —
    // the local "What's new?" answer is a complete exchange that never becomes a ticket.
    activeId: null,
    messages: [],
    messagesLoading: false,

    // What the support AI is doing right now, as `ai_progress` reports it: "Processing",
    // "Planning response", "Generating response". Cleared when the answer lands.
    progress: [],

    sending: false,
    failed: false,

    // Files the dealer has picked but not yet sent: each is uploaded on pick, so by the time
    // they hit Enter the ids are ready and the message goes in one call.
    //   { tempId, name, size, contentType, previewUrl, status, attachmentId, error }
    attachments: [],

    // Handing the thread to a human, and the "Did this resolve your issue?" answer.
    escalating: false,
    escalated: false,
    // The action in flight, so only the button that was pressed shows a spinner.
    resolving: null,
    // Answered or dismissed — either way the prompt doesn't come back for this thread.
    resolutionAnswered: false,

    // Torn down with the panel so a closed hub holds no socket handlers.
    stopListening: [],
  }),

  getters: {
    // A thread is open once the dealer opens one (its messages may still be loading) or says something support doesn't have yet.
    inConversation: (state) => !!state.activeId || state.messages.length > 0,

    // The open thread's own conversation record, for its ticket number and subject.
    activeConversation: (state) => state.conversations.find((conversation) => conversation.id === state.activeId) ?? null,

    // Replies the dealer hasn't read, across every conversation — the number the launcher
    // used to carry. It comes straight off each conversation row, so it needs nothing from
    // the support app (their `onUnreadChange` callback is not required for this).
    unreadCount: (state) => state.conversations.reduce((total, conversation) => total + (Number(conversation.unreadCount) || 0), 0),

    // Attachment ids that finished uploading, in pick order — what a message carries.
    readyAttachmentIds: (state) => state.attachments.filter((file) => file.attachmentId).map((file) => file.attachmentId),

    // Something is still uploading, so sending now would drop it from the message.
    uploading: (state) => state.attachments.some((file) => file.status === 'uploading'),

    /**
     * Whether to ask "Did this resolve your issue?".
     *
     * Only once the assistant has actually answered something — asking before a reply lands
     * is asking about nothing — and never on a thread already handed to a human or closed.
     */
    showResolution: (state) => {
      if (state.resolutionAnswered || state.escalated || state.sending || !state.activeId) return false
      const last = state.messages.at(-1)
      return !!last && last.senderType !== SUPPORT_SENDER.CUSTOMER && !last.localOnly
    },
  },

  actions: {
    /**
     * A usable session, reused across reloads. Anonymous first — the API mints the token —
     * then tied to the CDP dealer so an agent sees who is asking and the dealer's history
     * follows them to another device.
     *
     * `identify-email` is the whole identity story: the widget's signed-token path
     * (`/widget/identify`) is unwired on both sides — our backend mints no such token and we
     * serve no `/sso/silent-token` page — so there is nothing to send it.
     */
    async ensureSession() {
      const userStore = useUserStore()
      const email = userStore.user?.email ?? null

      if (this.sessionToken && this.identifiedAs === email) return this.sessionToken

      try {
        if (!this.sessionToken) {
          const { data } = await apiSupport().post('/widget/anonymous-session')
          this.setSession(data?.sessionToken ?? null)
        }

        if (email && this.identifiedAs !== email) {
          const { data } = await apiSupport().post('/widget/identify-email', {
            email,
            name: [userStore.user?.first_name, userStore.user?.last_name].filter(Boolean).join(' ') || email,
            userId: String(userStore.user?.id ?? ''),
            anonymousSessionToken: this.sessionToken || undefined,
          })

          // Identifying mints a NEW token for the now-named customer; keeping the anonymous
          // one would leave us reading an empty history beside the dealer's real one.
          if (data?.sessionToken) this.setSession(data.sessionToken)
          this.identifiedAs = email
        }

        return this.sessionToken
      } catch (error) {
        handleApiError(error, { component: 'SupportStore', action: 'ensureSession' })
        this.failed = true
        return null
      }
    },

    setSession(token) {
      this.sessionToken = token
      if (token) localStorage.setItem(SUPPORT_SESSION_KEY, token)
      else localStorage.removeItem(SUPPORT_SESSION_KEY)
    },

    /**
     * Just enough for the header's unread dot: a session and the conversation list.
     *
     * Separate from `load()` because it runs for every dealer on every app load, whether or
     * not they ever open the panel — an unread badge that only appears after you open the
     * thing it is telling you to open would be pointless. Once per app load; the session
     * token is cached in localStorage, so after the first visit this is a single GET.
     */
    async loadUnread() {
      if (this.unreadRequested) return
      this.unreadRequested = true

      this.listen()
      const token = await this.ensureSession()
      if (token) await this.fetchConversations()
    },

    /** Everything the Chat tab's landing view shows, in one call from the component. */
    async load() {
      this.listen()
      const token = await this.ensureSession()
      if (!token) return

      await Promise.all([this.fetchConversations(), this.fetchQuestions()])
    },

    async fetchConversations() {
      this.conversationsLoading = true
      this.conversationsFailed = false

      try {
        const { data } = await apiSupport(this.sessionToken).get('/widget/conversations')
        this.conversations = Array.isArray(data) ? data : []
        this.conversationsLoaded = true
        this.watchOpenConversations()
      } catch (error) {
        handleApiError(error, { component: 'SupportStore', action: 'fetchConversations' })
        this.conversationsFailed = true
      } finally {
        this.conversationsLoading = false
      }
    },

    /**
     * The "Popular questions" rows. The endpoint returns bare strings — no id, no article,
     * no view count — so a question is only ever something to ASK, which is what tapping one
     * does. (The mockup's "1.2k views" has no source on this API.)
     */
    async fetchQuestions() {
      try {
        const { data } = await apiSupport(this.sessionToken).get('/widget/kb/sample-questions')
        this.questions = Array.isArray(data?.questions) ? data.questions : []
      } catch (error) {
        handleApiError(error, { component: 'SupportStore', action: 'fetchQuestions' })
        this.questions = []
      }
    },

    async openConversation(id) {
      if (!id) return

      this.activeId = id
      this.messages = []
      this.progress = []
      this.escalated = false
      this.resolutionAnswered = false
      this.failed = false
      this.clearAttachments()
      this.messagesLoading = true

      try {
        const { data } = await apiSupport(this.sessionToken).get(`/widget/conversations/${id}/messages`)
        // Back was pressed while this loaded, so the thread is closed and its messages are not wanted.
        if (this.activeId !== id) return
        this.messages = Array.isArray(data) ? data : []
        subscribeToConversation(id)
        // Fire and forget: failing to clear the unread flag must not blank the thread.
        apiSupport(this.sessionToken).post(`/widget/conversations/${id}/read`).catch(() => {})
        // Locally too, so the header dot and the row's count drop as the thread opens
        // rather than on the next fetch.
        this.conversations = this.conversations.map((conversation) => (
          conversation.id === id ? { ...conversation, unreadCount: 0 } : conversation
        ))
      } catch (error) {
        handleApiError(error, { component: 'SupportStore', action: 'openConversation' })
        if (this.activeId === id) this.failed = true
      } finally {
        // A thread closed while it loaded has reset this itself.
        if (this.activeId === id) this.messagesLoading = false
      }
    },

    /** Back to the landing view; the thread stays on the server, only this view is dropped. */
    closeConversation() {
      if (this.activeId) unsubscribeFromConversation(this.activeId)
      this.activeId = null
      this.messages = []
      this.messagesLoading = false
      this.progress = []
      this.escalated = false
      this.resolutionAnswered = false
      this.clearAttachments()
      this.failed = false
      // The thread was just read; its row should stop claiming unread replies.
      this.fetchConversations()
    },

    /**
     * Send the dealer's line.
     *
     * "What's new?" is answered HERE rather than by the support AI, because the answer is
     * ours: the changelog the Help tab already loads from Strapi is the current one, and the
     * support service has no changelog at all. It is the one intent handled locally — the
     * exchange stays in the panel and never opens a ticket an agent would have to close.
     */
    async send(text) {
      const content = (text ?? '').trim()
      // A message may be files alone, but never nothing — and never while one is still uploading.
      if ((!content && !this.readyAttachmentIds.length) || this.sending || this.uploading) return

      this.failed = false

      // Answered from our own changelog, so neither side of this exchange ever reaches the
      // server and both have to be marked as the client's alone.
      if (isWhatsNewAsk(content)) {
        this.appendLocal({ senderType: SUPPORT_SENDER.CUSTOMER, content }, true)
        await this.answerWhatsNew()
        return
      }

      // The echo carries the pending files so they show in the thread before the server
      // answers; the server's own copy replaces it on the next refresh.
      this.appendLocal({
        senderType: SUPPORT_SENDER.CUSTOMER,
        content,
        attachments: this.attachments.filter((file) => file.attachmentId).map((file) => ({
          id: file.attachmentId, fileName: file.name, contentType: file.contentType, url: file.previewUrl,
        })),
      })
      this.progress = []
      this.sending = true
      // A new question reopens the resolution prompt; the dealer is asking about this answer.
      this.resolutionAnswered = false

      try {
        const token = await this.ensureSession()
        if (!token) return

        const attachmentIds = this.readyAttachmentIds

        if (this.activeId) {
          await apiSupport(token).post(`/widget/conversations/${this.activeId}/messages`, {
            content,
            ...(attachmentIds.length ? { attachmentIds } : {}),
          })
        } else {
          const { data } = await apiSupport(token).post('/widget/conversations', {
            subject: supportSubject(content) || 'Attachment',
            initialMessage: content,
            pageContext: { route: window.location.pathname },
            ...(attachmentIds.length ? { attachmentIds } : {}),
          })

          this.activeId = data?.id ?? null
          if (this.activeId) subscribeToConversation(this.activeId)
          this.fetchConversations()
        }

        // Starts the assistant's answer; it arrives over the socket, and the refetch below
        // covers the case where the socket never connected.
        if (this.activeId) {
          await apiSupport(token).post(`/widget/conversations/${this.activeId}/ai-assist`).catch(() => {})
          await this.refreshMessages()
        }
      } catch (error) {
        handleApiError(error, { component: 'SupportStore', action: 'send' })
        this.failed = true
      } finally {
        this.sending = false
        this.progress = []
        this.clearAttachments()
      }
    },

    /**
     * Hand the thread to a human. The support service queues it for an agent; the dealer is
     * told so in the thread, because nothing else on screen would change.
     */
    async escalate() {
      if (!this.activeId || this.escalating || this.escalated) return

      this.escalating = true

      try {
        await apiSupport(this.sessionToken).post(`/widget/conversations/${this.activeId}/escalate`)
        this.escalated = true
        this.resolutionAnswered = true
        await this.refreshMessages()
        this.fetchConversations()
      } catch (error) {
        handleApiError(error, { component: 'SupportStore', action: 'escalate' })
        this.failed = true
      } finally {
        this.escalating = false
      }
    },

    /**
     * Answer "Did this resolve your issue?" — `confirm_resolved`, `not_resolved` or
     * `request_human` (SUPPORT_RESOLUTION). The prompt goes either way: a dealer who answered
     * should not be asked again, even if the call failed.
     */
    async resolve(action) {
      if (!this.activeId || this.resolving) return

      this.resolving = action

      try {
        await apiSupport(this.sessionToken).post(`/widget/conversations/${this.activeId}/resolution`, { action })
        if (action === SUPPORT_RESOLUTION.REQUEST_HUMAN) this.escalated = true
        await this.refreshMessages()
        this.fetchConversations()
      } catch (error) {
        handleApiError(error, { component: 'SupportStore', action: 'resolve' })
      } finally {
        this.resolving = null
        this.resolutionAnswered = true
      }
    },

    /** The dealer closed the prompt without answering; don't ask again this thread. */
    dismissResolution() {
      this.resolutionAnswered = true
    },

    /**
     * Pick files. Each is uploaded immediately so the ids are ready when the message is sent,
     * and a file the service would reject is refused here, with the reason, rather than sent
     * and bounced.
     */
    attachFiles(files) {
      const room = SUPPORT_ATTACHMENT_MAX_FILES - this.attachments.length
      if (room <= 0) return

      Array.from(files).slice(0, room).forEach((file) => this.uploadAttachment(file))
    },

    async uploadAttachment(file) {
      const contentType = attachmentContentType(file)
      const rejection = attachmentRejection(file)
      const tempId = `file-${Date.now()}-${this.attachments.length}`

      const entry = {
        tempId,
        name: file.name,
        size: file.size,
        contentType,
        // Shown while it uploads; revoked when the entry goes, or the blob leaks.
        previewUrl: !rejection && contentType.startsWith('image/') ? URL.createObjectURL(file) : null,
        status: rejection ? 'error' : 'uploading',
        attachmentId: null,
        error: rejection,
      }

      this.attachments = [...this.attachments, entry]
      if (rejection) return

      const patch = (changes) => {
        this.attachments = this.attachments.map((item) => (item.tempId === tempId ? { ...item, ...changes } : item))
      }

      try {
        const token = await this.ensureSession()
        if (!token) throw new Error('No support session')

        // Three steps, and all three are theirs: ask for a signed URL, PUT the bytes straight
        // to storage, then register the object as an attachment to reference by id.
        const { data: slot } = await apiSupport(token).post('/widget/storage/uploads/request-url', {
          name: file.name,
          size: file.size,
          contentType,
        })

        if (!slot?.uploadURL || !slot?.objectPath) throw new Error('Bad upload URL response')

        await fetch(slot.uploadURL, {
          method: 'PUT',
          headers: { 'Content-Type': contentType || 'application/octet-stream' },
          body: file,
        }).then((response) => {
          if (!response.ok) throw new Error(`Upload failed (${response.status})`)
        })

        const { data: attachment } = await apiSupport(token).post('/widget/attachments', {
          fileName: file.name,
          objectPath: slot.objectPath,
          fileSize: file.size,
          contentType,
        })

        patch({ status: 'ready', attachmentId: attachment?.id ?? null })
      } catch (error) {
        handleApiError(error, { component: 'SupportStore', action: 'uploadAttachment' })
        patch({ status: 'error', error: "That file didn't upload" })
      }
    },

    removeAttachment(tempId) {
      const entry = this.attachments.find((file) => file.tempId === tempId)
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl)
      this.attachments = this.attachments.filter((file) => file.tempId !== tempId)
    },

    clearAttachments() {
      this.attachments.forEach((file) => file.previewUrl && URL.revokeObjectURL(file.previewUrl))
      this.attachments = []
    },

    async answerWhatsNew() {
      const strapiStore = useStrapiStore()
      if (!strapiStore.changeLog.length) await strapiStore.fetchChangeLogData()

      const notes = releaseNotesFromChangelog(strapiStore.changeLog)

      this.appendLocal(notes.length
        ? { senderType: SUPPORT_SENDER.AI, card: { type: 'releaseNotes', notes } }
        : { senderType: SUPPORT_SENDER.AI, content: "I couldn't load the release notes just now." }, true)
    },

    /**
     * Reconcile with the server. The server is authoritative for anything it has, so the
     * optimistic echo of the dealer's line is DROPPED here — keeping it is what showed the
     * question twice, once as the placeholder and once as the server's own copy.
     *
     * Only `localOnly` messages survive: the "What's new?" exchange, which never reaches the
     * server and so has no copy coming back.
     */
    async refreshMessages() {
      if (!this.activeId) return

      try {
        const { data } = await apiSupport(this.sessionToken).get(`/widget/conversations/${this.activeId}/messages`)
        if (!Array.isArray(data)) return

        this.messages = [...this.messages.filter((message) => message.localOnly), ...data]
          .sort((a, b) => new Date(a.createdAt ?? 0) - new Date(b.createdAt ?? 0))
      } catch (error) {
        handleApiError(error, { component: 'SupportStore', action: 'refreshMessages' })
      }
    },

    /**
     * A message drawn from the client alone.
     *
     * `localOnly` marks an exchange the server will never have — the release-notes answer and
     * the question that asked for it. Everything else is an optimistic echo that the next
     * refresh replaces with the server's copy.
     */
    appendLocal(message, localOnly = false) {
      this.messages = [...this.messages, {
        id: `local-${Date.now()}-${this.messages.length}`,
        createdAt: new Date().toISOString(),
        local: true,
        localOnly,
        ...message,
      }]
    },

    /**
     * Subscribe to every conversation still open, so a reply lands as an unread count even
     * when the panel is shut. Resolved and closed threads are skipped — nothing new arrives
     * on them, and each subscription is a frame on their socket.
     */
    watchOpenConversations() {
      this.conversations.filter(isOpenConversation).forEach((conversation) => subscribeToConversation(conversation.id))
    },

    /** Live replies. Registered once; the panel calls `teardown` when it unmounts. */
    listen() {
      if (this.stopListening.length) return

      initSupportSocket(() => this.sessionToken)

      this.stopListening = [
        onSupportMessage((message) => {
          if (!message?.id) return

          // A reply on a thread the dealer isn't looking at is what the header dot is for.
          // Without this the count is only ever as fresh as the last page load.
          if (message.conversationId !== this.activeId) {
            if (message.senderType === SUPPORT_SENDER.CUSTOMER) return
            this.conversations = this.conversations.map((conversation) => (
              conversation.id === message.conversationId
                ? { ...conversation, unreadCount: (Number(conversation.unreadCount) || 0) + 1, lastMessageAt: message.createdAt ?? conversation.lastMessageAt }
                : conversation
            ))
            return
          }

          if (this.messages.some((existing) => existing.id === message.id)) return

          // The dealer's own line is already on screen optimistically; drop that placeholder
          // as the server's copy of it arrives, so it isn't shown twice. A `localOnly` line
          // is never echoed by the server, so it is never a placeholder for one.
          const withoutEcho = message.senderType === SUPPORT_SENDER.CUSTOMER
            ? this.messages.filter((existing) => !(existing.local && !existing.localOnly && existing.content === message.content))
            : this.messages

          this.messages = [...withoutEcho, message]
          // The answer has landed, so the progress steps that were counting up to it go.
          this.progress = []
        }),

        // "Processing" → "Planning response" → "Generating response", as the support AI works.
        onSupportProgress((id, step, detail) => {
          if (id !== this.activeId || !step) return
          if (this.progress.some((entry) => entry.step === step)) return

          this.progress = [...this.progress, { step, detail: detail ?? '' }]
        }),

        onSupportStatus((id, status) => {
          this.conversations = this.conversations.map((conversation) => (
            conversation.id === id ? { ...conversation, status } : conversation
          ))
        }),
      ]
    },

    teardown() {
      this.stopListening.forEach((stop) => stop())
      this.stopListening = []
      closeSupportSocket()
    },
  },
})

// Without this a store edit does NOT reach a tab that already created the store: the module
// reloads but the live instance keeps its old actions, so a fix looks like it did nothing.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSupportStore, import.meta.hot))
}
